import React, { useState } from 'react';
import { styled } from 'themes';
import { useStaticQuery, graphql } from 'gatsby';
import Img from 'gatsby-image';
import Image from './pattern-group.svg';
import { useTranslation } from 'react-i18next';
import { device } from 'themes/media-queries';
import YepstrLogo from 'components/logos/yepstr-logo';
import { ROUTES } from 'navigation/route-map';
import { Link } from 'components';
import { colors } from 'themes/theme';

const HeaderSection = () => {
  const data = useStaticQuery(graphql`
    query HeaderQuery {
      mockImage: imageSharp(
        fluid: { originalName: { regex: "/mock-group/" } }
      ) {
        fluid(maxWidth: 1080, quality: 90) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
      promoImage: imageSharp(
        fluid: { originalName: { regex: "/idg-promo/" } }
      ) {
        fluid(maxWidth: 1080, quality: 100) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  `);

  const { t } = useTranslation();
  const [zipCode, setZipCode] = useState('');

  function onSubmit(e) {
    e.preventDefault();
    window.location.href = `https://karta.yepstr.com/utforska?postalCode=${zipCode}`;
  }

  return (
    <Header>
      <HeadingWrapper>
        <Logo />
        <Title>{t('header.title')}</Title>
        <Subtitle>{t('header.subtitle')}</Subtitle>
        <CTAContainer>
          <p>Se vilka duktiga yeppar som finns i ditt kvarter</p>
          <form onSubmit={onSubmit}>
            <input
              type="number"
              onChange={e => setZipCode(e.target.value)}
              placeholder="Postnummer"
            />
            <button>Sök</button>
          </form>
          <Link to={ROUTES.YEP}>Jag är 15-21 år och söker jobb</Link>
        </CTAContainer>
        {/* <Button label={t('header.buttonLabel')} to={ROUTES.DOWNLOAD} /> */}
      </HeadingWrapper>
      <ImageWrapper>
        <MockImage fluid={data.mockImage.fluid} />
        <PromoWrapper>
          <PromoImage fluid={data.promoImage.fluid} />
        </PromoWrapper>
      </ImageWrapper>
    </Header>
  );
};

const Header = styled.header`
  position: relative;
  grid-column: center-start / center-end;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(32rem, 1fr));
  grid-template-rows: 1fr;
  grid-gap: 3rem;
  align-items: start;

  background: url(${Image});
  background-repeat: no-repeat;
  background-size: contain;

  @media ${device.laptopL} {
    grid-column: center-start / full-end;
  }

  @media ${device.tablet} {
    grid-column: center-start / center-end;
  }
`;

const HeadingWrapper = styled.div`
  padding-top: 25rem;

  @media ${device.mobileL} {
    padding-top: 20rem;
  }
`;

const Title = styled.h1`
  margin-bottom: 16px;

  animation-name: moveInLeft;
  animation-duration: 1s;
  animation-timing-function: ease-out;

  @keyframes moveInLeft {
    0% {
      opacity: 0;
      transform: translateX(-10rem);
    }

    80% {
      transform: translateX(1rem);
    }

    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media ${device.mobileL} {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 2.4rem;
  margin-bottom: 2.5rem;

  animation: moveInRight 1s ease-out;

  @keyframes moveInRight {
    0% {
      opacity: 0;
      transform: translateX(10rem);
    }

    80% {
      transform: translateX(-1rem);
    }

    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media ${device.mobileL} {
    display: none;
  }
`;

const CTAContainer = styled.div`
  padding: 2.4rem;
  border-radius: 8px;
  box-shadow: 0 14px 24px 0 rgba(0, 0, 0, 0.06),
    0 6px 12px 0 rgba(0, 0, 0, 0.08);

  @media ${device.mobileL} {
    padding: 0;
    border-radius: 0;
    box-shadow: none;
  }

  p {
    margin-bottom: 2rem;
    font-size: 20px;

    @media ${device.mobileL} {
      margin-bottom: 16px;
      font-size: 16px;
    }
  }

  form {
    display: flex;
    border: 1px solid ${colors.silver};
    margin-bottom: 1.6rem;
    border-radius: 4px;
    overflow: hidden;

    @media ${device.mobileL} {
      margin-bottom: 24px;
    }
  }
  input {
    padding: 0 1.4rem;
    font-size: 16px;
    border: none;
    flex: 1;
    height: 50px;
  }
  input:focus {
    outline: none;
  }
  button {
    &,
    &:link,
    &:visited {
      position: relative;
      text-decoration: none;
      margin: 3px;
      padding: 1rem 4rem;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 4px;
      transition: all 0.2s;
      font-size: 2rem;
      font-weight: 700;
      color: white;
      white-space: nowrap;
      background-image: ${({ theme }) =>
        `linear-gradient(258deg, ${theme.colors.orange}, ${theme.colors.darkOrange})`};
      box-shadow: 0 2px 6px 0 rgba(43, 44, 61, 0.1),
        0 1px 2px 0 rgba(0, 0, 0, 0.12);
      user-select: none;
      border: none;
      cursor: pointer;
    }

    &:hover {
      box-shadow: none;

      &::after {
        opacity: 0;
      }
    }

    &:active,
    &:focus {
      outline: none;
      transform: translateY(0.1rem);
    }
  }

  a {
    text-decoration: none;
    color: ${colors.darkOrange};
    font-weight: 700;
    font-size: 16px;

    @media ${device.mobileL} {
      font-size: 14px;
    }
  }
`;

// const GetStartedButton = styled(Button)`
//   display: inline-block;
//   animation: moveInBottom 0.5s ease-out 0.75s;
//   animation-fill-mode: backwards;

//   @keyframes moveInBottom {
//     0% {
//       opacity: 0;
//       transform: translateY(3rem);
//     }

//     100% {
//       opacity: 1;
//       transform: translateY(0);
//     }
//   }
// `;

const Logo = styled(YepstrLogo)`
  position: absolute;
  top: 7.5rem;
  left: 0rem;

  @media ${device.mobileL} {
    top: 3rem;
  }
`;

const ImageWrapper = styled.div`
  position: relative;

  @media ${device.laptopL} {
    align-self: center;
  }
`;

const MockImage = styled(Img)`
  width: 100%;
  height: 100%;
`;

const PromoWrapper = styled.div`
  position: absolute;
  top: 40%;
  right: 55%;

  @media ${device.laptop} {
    top: 40%;
    right: 25%;
  }

  @media ${device.mobileL} {
    top: 10%;
    left: 0%;
  }
`;

const PromoImage = styled(Img)`
  width: 300px;
  height: 300px;

  @media ${device.laptop} {
    width: 250px;
    height: 250px;
  }

  @media ${device.mobileL} {
    width: 200px;
    height: 200px;
  }
`;

export default HeaderSection;
